/**
 * Dev Settings modal — local development only (spec 0111, Part B).
 *
 * Two independent selects mirror the real ABAC model:
 *   Level          → UserLevel → is_director_or_higher
 *   Functional Group → UserRole  → is_product_management / is_project_management
 *
 * This component is absent from production bundles.
 */

import { Alert, Button, Modal, Select, Space, Typography } from 'antd';
import React, { useState } from 'react';

import { LEVEL_OPTIONS, ROLE_OPTIONS } from '@/hooks/devRoleLabels';
import type { DevAssumedLevel, DevAssumedRole, DevRoleState } from '@/hooks/useDevRoleOverride';

const { Text, Paragraph } = Typography;

function resolveAccess(level: DevAssumedLevel, role: DevAssumedRole): string {
  const isDirector = level === 'director' || level === 'vp';
  const isPM = role === 'pm' || role === 'project_management';
  const isClientMgmt = role === 'client_management';
  if (isDirector && (isPM || isClientMgmt)) {
    return 'Metric access: enabled (Director + PM/Client Mgmt)';
  }
  if (isDirector) {
    return 'Metric access: enabled (Director or above)';
  }
  if (isPM) {
    return 'Metric access: enabled (Product/Project Management)';
  }
  if (isClientMgmt) {
    return 'Metric access: enabled (Client Management)';
  }
  return 'Metric access: restricted';
}

export interface DevSettingsModalProps {
  open: boolean;
  currentRoleState: DevRoleState | null;
  onApply: (state: DevRoleState) => void;
  onClear: () => void;
  onCancel: () => void;
}

export const DevSettingsModal: React.FC<DevSettingsModalProps> = ({
  open,
  currentRoleState,
  onApply,
  onClear,
  onCancel,
}) => {
  const [level, setLevel] = useState<DevAssumedLevel>(currentRoleState?.level ?? 'none');
  const [role, setRole] = useState<DevAssumedRole>(currentRoleState?.role ?? 'none');

  const handleOk = () => {
    onApply({ level, role });
  };

  const handleClear = () => {
    setLevel('none');
    setRole('none');
    onClear();
  };

  const accessSummary = resolveAccess(level, role);
  const hasAccess = !accessSummary.includes('restricted');

  return (
    <Modal
      title="Dev Settings — Role Override"
      open={open}
      onCancel={onCancel}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button danger onClick={handleClear}>
            Clear override
          </Button>
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="primary" onClick={handleOk}>
              Save settings
            </Button>
          </Space>
        </div>
      }
      destroyOnHidden
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="warning"
          showIcon
          message="Local development only"
          description="In local development this override applies to both UI rendering and back-end field access. In deployed environments the cookie has no effect — real JWT roles apply."
        />

        <div>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>
            Level
          </Text>
          <Select<DevAssumedLevel>
            value={level}
            onChange={setLevel}
            style={{ width: '100%' }}
            options={LEVEL_OPTIONS}
          />
        </div>

        <div>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>
            Functional Group
          </Text>
          <Select<DevAssumedRole>
            value={role}
            onChange={setRole}
            style={{ width: '100%' }}
            options={ROLE_OPTIONS}
          />
        </div>

        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {currentRoleState ? (
            <>
              Active override:{' '}
              {LEVEL_OPTIONS.find((o) => o.value === currentRoleState.level)?.label} ·{' '}
              {ROLE_OPTIONS.find((o) => o.value === currentRoleState.role)?.label}
              <br />
            </>
          ) : (
            'Using real role from JWT token · '
          )}
          <Text type={hasAccess ? 'success' : 'secondary'}>{accessSummary}</Text>
        </Paragraph>
      </Space>
    </Modal>
  );
};

export default DevSettingsModal;
